// Client settings (localStorage-backed). Applied live: render distance drives
// streaming, GUI scale zooms the UI, invert-Y flips the mouse, the volume sliders
// drive the audio engine's master/sound/music gains (AUD-1), `subtitles` toggles
// the sound-event caption overlay (M23 accessibility), and `keybinds` remaps the
// movement/menu keys (M24, honored by input.ts).
import { VIEW } from '../sim/config';
import { DEFAULT_BINDS, normalizeBinds, type Binds } from './keybinds';
import { clampFov, FOV_DEFAULT } from './fov';

export interface Settings {
  renderDistance: number;
  fov: number;
  guiScale: number;
  invertY: boolean;
  masterVolume: number;
  musicVolume: number;
  soundVolume: number;
  subtitles: boolean;
  keybinds: Binds;
}

const KEY = 'bb.settings';
const DEFAULTS: Settings = {
  renderDistance: VIEW.RENDER_DIST_DEFAULT,
  fov: FOV_DEFAULT,
  guiScale: 1,
  invertY: false,
  masterVolume: 1,
  musicVolume: 0.5,
  soundVolume: 1,
  subtitles: false,
  keybinds: { ...DEFAULT_BINDS },
};

export function loadSettings(): Settings {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}');
    const s = { ...DEFAULTS, ...raw };
    s.renderDistance = Math.max(4, Math.min(VIEW.RENDER_DIST_MAX, s.renderDistance | 0));
    s.fov = clampFov(s.fov);
    s.keybinds = normalizeBinds(raw.keybinds); // fill any missing/new actions from defaults
    return s;
  } catch {
    return { ...DEFAULTS, keybinds: { ...DEFAULT_BINDS } };
  }
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode — settings just won't persist */
  }
}
